import styled from "styled-components";
import { Notice, ProgressBar, Totalizers } from "@ficatranquilo/contabilidade.com-ui";
import { fixedAdjustmentCompetence } from "@helpers/fixedAdjustmentCompetence";
import { formatBRL, formatCompetencia, formatHours } from "../ow";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** Horas decimais da API ("167.5") no padrão do design system ("167h30"); nunca decimal. */
const hrs = (value) => formatHours((Number(value) || 0) * 60);

/** Competência seguinte, virando o ano em dezembro ("Nov/2026"). */
function nextCompetence(schedule) {
  const month = Number(schedule.reference_month);
  const year = Number(schedule.reference_year);
  return formatCompetencia(month === 12 ? { month: 1, year: year + 1 } : { month: month + 1, year });
}

/**
 * Composição do valor de um cronograma no modo de valor fixo com ajuste por horas:
 * nos detalhes do pagamento do ADM e do prestador.
 *
 * Existe como componente porque o mesmo bloco aparece nas duas visões, e já foi essa
 * duplicação — a mesma regra escrita em dois lugares — que fez a lista e os detalhes
 * do ADM divergirem antes.
 *
 * Só "Horas no pagamento" e a jornada: a conta corrente por trás (medidas, adiantadas,
 * já cobradas) não vai para a tela. A barra compara as duas: laranja enquanto faltam
 * horas, verde com a jornada cumprida. Fora do modo de valor fixo não renderiza nada.
 *
 * Props:
 * - schedule: cronograma como vem da API
 * - period: janela de apuração já resolvida pela tela, quando houver
 */
export default function CompetenceValueBlock({ schedule, period }) {
  const competence = fixedAdjustmentCompetence(schedule, period);
  if (!competence) return null;

  const balance = competence.balanceHours;
  const balanceColor = balance < 0 ? "E400" : balance > 0 ? "P800" : "N900";
  const sign = balance < 0 ? "− " : balance > 0 ? "+ " : "";

  const totals = [
    { name: "Horas no pagamento", value: hrs(competence.creditedHours), color: "N900" },
    { name: "Jornada esperada", value: hrs(competence.expectedHours), color: "N900" },
    { name: "Saldo do período", value: `${balance > 0 ? "+" : ""}${hrs(balance)}`, color: balanceColor },
    { name: "Valor fixo", value: formatBRL(competence.proratedFixed), color: "N900" },
    { name: "Saldo em valor", value: `${sign}${formatBRL(competence.balanceValue)}`, color: balanceColor },
  ];
  // Competência aberta: o valor ainda muda a cada aprovação de horas
  if (competence.isOpen) {
    totals.push({ name: "Apurado até agora", value: formatBRL(competence.partialValue), color: "N900" });
  }

  const notes = [];
  if (competence.isProrated) {
    notes.push(
      `O valor fixo é proporcional a ${schedule.active_business_days} de ${schedule.month_business_days} dias úteis do mês.`
    );
  }
  if (competence.isOpen && balance !== 0) {
    notes.push(`Fecha em <strong>${formatBRL(competence.targetValue)}</strong> com o saldo zerado.`);
  }
  if (competence.isOpen && balance < 0 && competence.cutoffLabel) {
    notes.push(
      `Conta a hora aprovada até ${competence.cutoffLabel}; o que for aprovado depois entra em ${nextCompetence(schedule)}.`
    );
  }
  // Congelada: o valor já é definitivo, mas a hora que faltou não virou dívida —
  // ela entra como crédito na competência seguinte, conforme for aprovada.
  if (competence.isFrozen && balance < 0) {
    notes.push(
      `Competência encerrada. As ${hrs(Math.abs(balance))} que faltaram entram em ${nextCompetence(schedule)} conforme forem aprovadas.`
    );
  }

  const expected = Number(competence.expectedHours) || 0;

  return (
    <Stack>
      <Totalizers totals={totals} />
      {expected > 0 ? (
        <ProgressBar
          $value={Number(competence.creditedHours) || 0}
          $max={expected}
          $variant={balance < 0 ? "orange" : "green"}
          $label="Horas no pagamento"
          $valueText={`${hrs(competence.creditedHours)} de ${hrs(expected)}`}
        />
      ) : null}
      {notes.length ? <Notice $variant="gray" $title="Composição do valor" $text={notes.join("<br />")} /> : null}
    </Stack>
  );
}
